import React, { useState, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { QRCodeCanvas, QRCodeSVG } from 'qrcode.react';
import { Mountain, ArrowLeft, Download, Copy, Check, Link2, MessageCircle, FileText } from 'lucide-react';

const pf = { fontFamily: "'Playfair Display', Georgia, serif" };
const ACCENT = '#3a5a40';
const SITE = 'https://gohimalayan.com';
const DEFAULT_WA_NUMBER = '919871155963';

const PRESETS = [
  { label: 'Landing page', path: '/', desc: 'gohimalayan.com' },
  { label: 'Curated Stays', path: '/discovered', desc: 'gohimalayan.com/discovered' },
  { label: 'AI Trip Planner', path: '/plan', desc: 'gohimalayan.com/plan' },
  { label: 'The Himalayan Store', path: '/store', desc: 'gohimalayan.com/store' },
  { label: 'Itineraries', path: '/itineraries', desc: 'gohimalayan.com/itineraries' },
  { label: 'Partner With Us', path: '/partner', desc: 'gohimalayan.com/partner' },
  { label: 'All Links', path: '/links', desc: 'gohimalayan.com/links' },
];

const WA_PRESETS = [
  { label: 'General enquiry', message: 'Hi GoHimalayan! I\'d like to know more.' },
  { label: 'Book a stay', message: 'Hi GoHimalayan! I\'d like to book a stay. Can you help me find the right one?' },
  { label: 'Plan a trip', message: 'Hi GoHimalayan! I\'d like to plan a Himalayan trip — please help me get started.' },
  { label: 'Partner enquiry', message: 'Hi GoHimalayan! I run a homestay and would like to discuss partnering with you.' },
  { label: 'Store order', message: 'Hi GoHimalayan! I\'d like to order from The Himalayan Store. Can you help?' },
];

export default function QR() {
  const [mode, setMode] = useState('url'); // 'url' or 'whatsapp'
  const [path, setPath] = useState('/');
  const [waNumber, setWaNumber] = useState(DEFAULT_WA_NUMBER);
  const [waMessage, setWaMessage] = useState(WA_PRESETS[0].message);
  const [size, setSize] = useState(400);
  const [includeLogo, setIncludeLogo] = useState(true);
  const [copied, setCopied] = useState(false);
  const canvasRef = useRef(null);
  const svgRef = useRef(null);

  const url = useMemo(() => {
    if (mode === 'whatsapp') {
      return `https://wa.me/${waNumber}?text=${encodeURIComponent(waMessage)}`;
    }
    return `${SITE}${path}`;
  }, [mode, path, waNumber, waMessage]);

  const displayUrl = useMemo(() => {
    if (mode === 'whatsapp') return `WhatsApp · +${waNumber}`;
    return url;
  }, [mode, url, waNumber]);

  const filenameSuffix = useMemo(() => {
    if (mode === 'whatsapp') return 'whatsapp';
    return path === '/' ? 'home' : path.replace(/\//g, '-').replace(/^-/, '');
  }, [mode, path]);

  const downloadPNG = () => {
    const canvas = canvasRef.current?.querySelector('canvas');
    if (!canvas) return;
    const link = document.createElement('a');
    link.download = `gohimalayan-qr-${filenameSuffix}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  };

  const downloadSVG = () => {
    const svg = svgRef.current?.querySelector('svg');
    if (!svg) return;
    const serializer = new XMLSerializer();
    const source = serializer.serializeToString(svg);
    const blob = new Blob([source], { type: 'image/svg+xml;charset=utf-8' });
    const link = document.createElement('a');
    link.download = `gohimalayan-qr-${filenameSuffix}.svg`;
    link.href = URL.createObjectURL(blob);
    link.click();
  };

  const copyUrl = () => {
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen bg-[#FAFAF8]" data-testid="qr-page">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/85 backdrop-blur-md border-b border-stone-100 print:hidden">
        <div className="max-w-6xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <Mountain className="h-7 w-7" style={{ color: ACCENT }} strokeWidth={2.5} />
            <span className="text-lg font-bold tracking-tight text-stone-900" style={pf}>GoHimalayan</span>
          </Link>
          <Link to="/" className="flex items-center gap-2 text-stone-500 hover:text-stone-900 transition-colors text-sm" data-testid="back-home-btn">
            <ArrowLeft className="h-4 w-4" />Home
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="pt-28 pb-8 px-6 print:hidden">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-[11px] tracking-[0.4em] uppercase font-semibold mb-3" style={{ color: ACCENT }}>QR Code Generator</p>
          <h1 className="text-3xl sm:text-4xl font-bold text-stone-900 mb-4" style={pf}>
            Take GoHimalayan offline.
          </h1>
          <p className="text-stone-500 text-base max-w-xl mx-auto" style={pf}>
            Generate a QR code for any page, or a deep-link that starts a WhatsApp conversation directly.
          </p>
          <Link to="/qr/starter-pack" className="inline-flex items-center gap-2 mt-6 px-5 py-2.5 bg-stone-900 text-white rounded-full text-sm font-semibold hover:bg-stone-700 transition-colors" data-testid="starter-pack-link">
            <FileText className="w-4 h-4" />Open Printable Starter Pack
          </Link>
        </div>
      </section>

      {/* Main */}
      <section className="pb-20 px-6">
        <div className="max-w-5xl mx-auto grid lg:grid-cols-2 gap-8">
          {/* Controls */}
          <div className="bg-white rounded-2xl p-7 border border-stone-100 print:hidden" data-testid="controls">
            {/* Mode toggle */}
            <div className="bg-stone-100 rounded-xl p-1 flex mb-6" data-testid="mode-toggle">
              <button onClick={() => setMode('url')} className={`flex-1 px-4 py-2 rounded-lg text-xs font-semibold transition-all ${mode === 'url' ? 'bg-white text-stone-900 shadow-sm' : 'text-stone-500'}`} data-testid="mode-url">
                <Link2 className="w-3.5 h-3.5 inline mr-1.5" />URL
              </button>
              <button onClick={() => setMode('whatsapp')} className={`flex-1 px-4 py-2 rounded-lg text-xs font-semibold transition-all ${mode === 'whatsapp' ? 'bg-white text-[#25D366] shadow-sm' : 'text-stone-500'}`} data-testid="mode-whatsapp">
                <MessageCircle className="w-3.5 h-3.5 inline mr-1.5" />WhatsApp
              </button>
            </div>

            {mode === 'url' ? (
              <>
                <p className="text-[10px] tracking-[0.3em] uppercase text-stone-400 font-semibold mb-4">Choose a destination</p>
                <div className="grid grid-cols-2 gap-2 mb-6">
                  {PRESETS.map((p, i) => (
                    <button
                      key={i}
                      onClick={() => setPath(p.path)}
                      className="text-left px-3 py-2.5 rounded-lg border transition-all"
                      data-testid={`preset-${i}`}
                      style={path === p.path ? { borderColor: ACCENT, backgroundColor: `${ACCENT}08` } : { borderColor: '#e7e5e4' }}
                    >
                      <p className="text-xs font-semibold text-stone-900">{p.label}</p>
                      <p className="text-[10px] text-stone-400 truncate">{p.desc}</p>
                    </button>
                  ))}
                </div>

                <p className="text-[10px] tracking-[0.3em] uppercase text-stone-400 font-semibold mb-2">Or custom path</p>
                <div className="flex items-center gap-2 mb-6">
                  <span className="text-xs text-stone-400 whitespace-nowrap">{SITE}</span>
                  <input
                    type="text"
                    value={path}
                    onChange={(e) => setPath(e.target.value.startsWith('/') ? e.target.value : '/' + e.target.value)}
                    className="flex-1 px-3 py-2 text-sm border border-stone-200 rounded-lg focus:border-[#3a5a40] focus:outline-none"
                    placeholder="/pitch/vision-himachal"
                    data-testid="custom-path-input"
                  />
                </div>
              </>
            ) : (
              <>
                <p className="text-[10px] tracking-[0.3em] uppercase text-stone-400 font-semibold mb-2">WhatsApp number</p>
                <div className="flex items-center gap-2 mb-6">
                  <span className="text-xs text-stone-400">+</span>
                  <input
                    type="text"
                    value={waNumber}
                    onChange={(e) => setWaNumber(e.target.value.replace(/\D/g, ''))}
                    className="flex-1 px-3 py-2 text-sm border border-stone-200 rounded-lg focus:border-[#25D366] focus:outline-none"
                    placeholder="919871155963"
                    data-testid="wa-number-input"
                  />
                </div>

                <p className="text-[10px] tracking-[0.3em] uppercase text-stone-400 font-semibold mb-3">Message presets</p>
                <div className="space-y-2 mb-4">
                  {WA_PRESETS.map((p, i) => (
                    <button
                      key={i}
                      onClick={() => setWaMessage(p.message)}
                      className="w-full text-left px-3 py-2.5 rounded-lg border transition-all"
                      data-testid={`wa-preset-${i}`}
                      style={waMessage === p.message ? { borderColor: '#25D366', backgroundColor: '#25D36608' } : { borderColor: '#e7e5e4' }}
                    >
                      <p className="text-xs font-semibold text-stone-900 mb-0.5">{p.label}</p>
                      <p className="text-[10px] text-stone-500 truncate">{p.message}</p>
                    </button>
                  ))}
                </div>

                <p className="text-[10px] tracking-[0.3em] uppercase text-stone-400 font-semibold mb-2">Or custom message</p>
                <textarea
                  value={waMessage}
                  onChange={(e) => setWaMessage(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:border-[#25D366] focus:outline-none mb-6 resize-none"
                  rows="2"
                  placeholder="Hi GoHimalayan!"
                  data-testid="wa-message-input"
                />
              </>
            )}

            <p className="text-[10px] tracking-[0.3em] uppercase text-stone-400 font-semibold mb-2">Size</p>
            <input
              type="range"
              min="200"
              max="800"
              step="50"
              value={size}
              onChange={(e) => setSize(Number(e.target.value))}
              className="w-full mb-2"
              data-testid="size-slider"
            />
            <p className="text-xs text-stone-500 mb-6">{size} × {size} px</p>

            <label className="flex items-center gap-3 cursor-pointer mb-6">
              <input
                type="checkbox"
                checked={includeLogo}
                onChange={(e) => setIncludeLogo(e.target.checked)}
                className="w-4 h-4 accent-[#3a5a40]"
                data-testid="logo-checkbox"
              />
              <span className="text-sm text-stone-700">Include GoHimalayan logo in centre</span>
            </label>

            <div className="border-t border-stone-100 pt-6">
              <p className="text-[10px] tracking-[0.3em] uppercase text-stone-400 font-semibold mb-3">Encoded {mode === 'whatsapp' ? 'WhatsApp link' : 'URL'}</p>
              <div className="flex items-center gap-2 mb-4 bg-stone-50 rounded-lg p-3">
                {mode === 'whatsapp' ? <MessageCircle className="w-4 h-4 text-[#25D366] flex-shrink-0" /> : <Link2 className="w-4 h-4 text-stone-400 flex-shrink-0" />}
                <span className="text-xs text-stone-700 truncate flex-1" data-testid="encoded-url">{url}</span>
                <button onClick={copyUrl} className="flex-shrink-0 text-[#3a5a40]" data-testid="copy-url-btn">
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <button onClick={downloadPNG} className="flex items-center justify-center gap-2 px-4 py-3 bg-stone-900 text-white rounded-lg text-sm font-semibold hover:bg-stone-700 transition-colors" data-testid="download-png">
                  <Download className="w-4 h-4" />PNG
                </button>
                <button onClick={downloadSVG} className="flex items-center justify-center gap-2 px-4 py-3 bg-white border border-stone-300 text-stone-900 rounded-lg text-sm font-semibold hover:bg-stone-50 transition-colors" data-testid="download-svg">
                  <Download className="w-4 h-4" />SVG
                </button>
              </div>
              <button onClick={() => window.print()} className="w-full mt-2 px-4 py-3 bg-white border border-stone-300 text-stone-900 rounded-lg text-sm font-semibold hover:bg-stone-50 transition-colors" data-testid="print-btn">
                Print this page
              </button>
            </div>
          </div>

          {/* Preview */}
          <div className="flex flex-col items-center justify-start lg:sticky lg:top-24 self-start" data-testid="qr-preview">
            <div className="bg-white rounded-2xl p-10 border border-stone-100 w-full" style={{ aspectRatio: '1' }}>
              <div className="h-full flex flex-col items-center justify-center">
                <div className="flex items-center gap-2 mb-6">
                  {mode === 'whatsapp' ? (
                    <MessageCircle className="w-5 h-5 text-[#25D366]" strokeWidth={2.5} />
                  ) : (
                    <Mountain className="w-5 h-5" style={{ color: ACCENT }} strokeWidth={2.5} />
                  )}
                  <span className="text-base font-bold tracking-tight text-stone-900" style={pf}>
                    {mode === 'whatsapp' ? 'WhatsApp GoHimalayan' : 'GoHimalayan'}
                  </span>
                </div>

                <div ref={canvasRef} className="hidden">
                  <QRCodeCanvas
                    value={url}
                    size={size}
                    bgColor="#FFFFFF"
                    fgColor={mode === 'whatsapp' ? '#075E54' : '#0D1B2A'}
                    level="H"
                    includeMargin={false}
                    imageSettings={includeLogo ? {
                      src: '/favicon.svg',
                      x: undefined,
                      y: undefined,
                      height: Math.round(size * 0.18),
                      width: Math.round(size * 0.18),
                      excavate: true,
                    } : undefined}
                  />
                </div>

                <div ref={svgRef} className="flex items-center justify-center" style={{ maxWidth: '100%' }}>
                  <QRCodeSVG
                    value={url}
                    size={Math.min(size, 320)}
                    bgColor="#FFFFFF"
                    fgColor={mode === 'whatsapp' ? '#075E54' : '#0D1B2A'}
                    level="H"
                    includeMargin={false}
                    imageSettings={includeLogo ? {
                      src: '/favicon.svg',
                      x: undefined,
                      y: undefined,
                      height: Math.round(Math.min(size, 320) * 0.18),
                      width: Math.round(Math.min(size, 320) * 0.18),
                      excavate: true,
                    } : undefined}
                  />
                </div>

                <p className="text-[10px] tracking-[0.3em] uppercase text-stone-400 font-semibold mt-6">
                  {mode === 'whatsapp' ? 'Scan to chat' : 'Scan to visit'}
                </p>
                <p className="text-sm text-stone-700 mt-1 truncate max-w-full" style={pf}>{displayUrl}</p>
              </div>
            </div>

            <p className="text-[11px] text-stone-400 text-center mt-4 max-w-sm print:hidden">
              QR code uses high error-correction, so the centre logo doesn't break scannability. Tested on iPhone, Android, and Google Lens.
            </p>
          </div>
        </div>
      </section>

      {/* Use cases */}
      <section className="pb-20 px-6 print:hidden">
        <div className="max-w-5xl mx-auto">
          <p className="text-[10px] tracking-[0.3em] uppercase text-stone-400 font-semibold mb-3 text-center">Where to use it</p>
          <h2 className="text-2xl font-bold text-stone-900 text-center mb-10" style={pf}>Designed for the offline world.</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { title: 'Property check-in', text: 'Print on a card placed in every featured homestay — guests scan to leave reviews or browse the curation.' },
              { title: 'Retail signage', text: 'Display at the GoHimalayan retail stores in Dubai, London, or Mumbai for instant browsing.' },
              { title: 'Pitch decks', text: 'Print on the back cover of physical pitch decks delivered to CMs and Tourism Secretaries.' },
              { title: 'Business cards', text: 'Tiny QR on the corner of cards — instantly opens GoHimalayan on the recipient\'s phone.' },
            ].map((u, i) => (
              <div key={i} className="bg-white rounded-xl p-5 border border-stone-100" data-testid={`use-${i}`}>
                <p className="text-sm font-bold text-stone-900 mb-2" style={pf}>{u.title}</p>
                <p className="text-xs text-stone-500 leading-relaxed">{u.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
